import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';

import { Button } from '~/shadcn/components/ui/Button';
import { Input } from '~/shadcn/components/ui/Input';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/shadcn/components/ui/tooltip';
import { cn } from '~/shadcn/lib/utils';

/**
 * Read-only cell renderer for data columns with error highlighting and tooltip
 */
export function DataCell({ column, getValue, row, table }) {
    const { errorsByCell, pendingDeletes } = table.options.meta;
    const { id: fieldKey } = column;
    const { rowIndex } = row.original;
    const value = getValue();
    const isDeleted = pendingDeletes?.has(rowIndex);

    const errorKey = `${rowIndex}:${fieldKey}`;
    const error = errorsByCell?.get(errorKey);

    const cellContent = (
        <div
            className={cn(
                'px-2 py-1 -mx-2 -my-1',
                error && !isDeleted && 'bg-red-100 border-2 border-red-500 rounded cursor-help',
                isDeleted && 'line-through text-muted-foreground',
            )}
        >
            {value ?? ''}
        </div>
    );

    if (error && !isDeleted) {
        return (
            <Tooltip>
                <TooltipTrigger asChild>{cellContent}</TooltipTrigger>
                <TooltipContent className="bg-red-100 text-black border-2 border-red-500 max-w-xs">
                    {error.message}
                </TooltipContent>
            </Tooltip>
        );
    }

    return cellContent;
}

/**
 * Editable cell renderer with click-to-edit, error display, and pending edit indicator
 */
export function EditableCell({ column, getValue, row, table }) {
    const { errorsByCell, pendingDeletes, pendingEdits, updateCell } = table.options.meta;
    const { id: fieldKey } = column;
    const { rowIndex } = row.original;
    const originalValue = getValue();
    const isDeleted = pendingDeletes?.has(rowIndex);

    // Check for pending edit value
    const pendingValue = pendingEdits?.[rowIndex]?.[fieldKey];
    const hasPendingEdit = pendingValue !== undefined;
    const displayValue = hasPendingEdit ? pendingValue : originalValue;

    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(displayValue ?? '');
    const inputRef = useRef(null);

    // Sync edit value when display value changes (e.g., after submit)
    useEffect(() => {
        if (!isEditing) {
            setEditValue(displayValue ?? '');
        }
    }, [displayValue, isEditing]);

    // Focus input when entering edit mode
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const errorKey = `${rowIndex}:${fieldKey}`;
    const error = errorsByCell?.get(errorKey);

    const handleClick = useCallback(() => {
        // Don't allow editing deleted rows
        if (!isEditing && !isDeleted) {
            setIsEditing(true);
            setEditValue(displayValue ?? '');
        }
    }, [isEditing, isDeleted, displayValue]);

    const handleBlur = useCallback(() => {
        setIsEditing(false);
        // Only update if value changed from original
        if (editValue !== (originalValue ?? '')) {
            updateCell?.(rowIndex, fieldKey, editValue);
        }
    }, [editValue, originalValue, rowIndex, fieldKey, updateCell]);

    const handleKeyDown = useCallback(
        (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleBlur();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                setEditValue(displayValue ?? '');
                setIsEditing(false);
            }
        },
        [handleBlur, displayValue],
    );

    const handleChange = useCallback((e) => {
        setEditValue(e.target.value);
    }, []);

    // Editing mode (not allowed when deleted)
    if (isEditing && !isDeleted) {
        return (
            <Input
                ref={inputRef}
                value={editValue}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                className="h-7 px-2 py-1 -mx-2 -my-1 text-sm"
            />
        );
    }

    // Display mode
    const cellContent = (
        <div
            role="button"
            tabIndex={isDeleted ? -1 : 0}
            onClick={handleClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick();
                }
            }}
            className={cn(
                'px-2 py-1 -mx-2 -my-1 rounded',
                isDeleted && 'line-through text-muted-foreground cursor-default',
                !isDeleted && 'cursor-pointer hover:bg-muted/50',
                error && !isDeleted && 'bg-red-100 border-2 border-red-500 cursor-help',
                hasPendingEdit && !error && !isDeleted && 'bg-yellow-100 border border-yellow-500',
            )}
        >
            {displayValue ?? ''}
        </div>
    );

    if (error && !isDeleted) {
        return (
            <Tooltip>
                <TooltipTrigger asChild>{cellContent}</TooltipTrigger>
                <TooltipContent className="bg-red-100 text-black border-2 border-red-500 max-w-xs">
                    {error.message}
                </TooltipContent>
            </Tooltip>
        );
    }

    return cellContent;
}

/**
 * Cell renderer for the row index column
 */
export function RowIndexCell({ row, table }) {
    const { pendingDeletes } = table.options.meta;
    const { rowIndex } = row.original;
    const isDeleted = pendingDeletes?.has(rowIndex);

    return <span className={cn('text-muted-foreground', isDeleted && 'line-through')}>{rowIndex + 1}</span>;
}

/**
 * Delete button cell for marking rows for deletion
 */
export function DeleteCheckboxCell({ row, table }) {
    const { pendingDeletes, toggleRowDelete } = table.options.meta;
    const { rowIndex } = row.original;
    const isMarkedForDeletion = pendingDeletes?.has(rowIndex) ?? false;

    const handleClick = useCallback(() => {
        toggleRowDelete?.(rowIndex);
    }, [rowIndex, toggleRowDelete]);

    return (
        <Button
            variant={isMarkedForDeletion ? 'destructive' : 'ghost'}
            size="icon"
            className="h-7 w-7"
            onClick={handleClick}
            aria-label={isMarkedForDeletion ? `Undo delete row ${rowIndex + 1}` : `Delete row ${rowIndex + 1}`}
            aria-pressed={isMarkedForDeletion}
        >
            <Trash2 className="h-4 w-4" />
        </Button>
    );
}
